/**
 * Data layer for `creative-max-50mb-upload`. Thin typed fetch wrappers over /api/v1/creative-max-50mb-upload.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CreativeMax50mbUploadRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCreativeMax50mbUploadResponse {
	count: number;
	items: CreativeMax50mbUploadRow[];
}

export interface CreateCreativeMax50mbUploadBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/creative-max-50mb-upload";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCreativeMax50mbUploadResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCreativeMax50mbUploadResponse>(res);
}

export async function create(body: CreateCreativeMax50mbUploadBody): Promise<CreativeMax50mbUploadRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CreativeMax50mbUploadRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const creativeMax50mbUploadQueryKeys = {
	all: ["creative-max-50mb-upload"] as const,
	list: () => [...creativeMax50mbUploadQueryKeys.all] as const,
	detail: (id: string) => [...creativeMax50mbUploadQueryKeys.all, id] as const,
};
